// frontend/src/auth.ts

export type JwtPayload = {
  sub: number;          // user id
  company_id?: number;  // opcional en algunos JWT
  roles?: string[];     // puede venir como array o faltar
  iat?: number;
  exp?: number;         // epoch seconds
  [k: string]: unknown; // permitir campos adicionales
};

/* ======================== Utils internos ======================== */

/** Decodifica Base64URL (JWT) a string UTF-8 seguro */
function base64UrlDecode(b64url: string): string {
  // Reemplazos Base64URL
  let b64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  // Padding
  const pad = b64.length % 4;
  if (pad === 2) b64 += '==';
  else if (pad === 3) b64 += '=';
  else if (pad === 1) throw new Error('Invalid base64url string');

  // atob -> bytes -> UTF-8
  const binary = atob(b64);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  // TextDecoder asegura UTF-8 correcto (evita errores con tildes)
  return new TextDecoder('utf-8').decode(bytes);
}

/** Parseo seguro de JSON */
function safeJSON<T = any>(str: string, fallback: T | null = null): T | null {
  try {
    return JSON.parse(str) as T;
  } catch {
    return fallback;
  }
}

/* ======================= Storage helpers ======================== */

/** Intenta leer el token desde localStorage y sessionStorage */
export function getToken(): string | null {
  return localStorage.getItem('token') ?? sessionStorage.getItem('token');
}

/**
 * Guarda el token en storage.
 * @param token JWT
 * @param remember true => localStorage, false => sessionStorage
 */
export function setToken(token: string, remember = true) {
  clearToken();
  if (remember) localStorage.setItem('token', token);
  else sessionStorage.setItem('token', token);
}

/** Elimina el token de ambos storages */
export function clearToken() {
  localStorage.removeItem('token');
  sessionStorage.removeItem('token');
}

/** Guarda el objeto de usuario (como JSON) */
export function setUser(user: unknown, remember = true) {
  const s = JSON.stringify(user ?? null);
  if (remember) localStorage.setItem('user', s);
  else sessionStorage.setItem('user', s);
}

/** Lee el usuario desde storage (si existe) */
export function getUser<T = any>(): T | null {
  const raw = localStorage.getItem('user') ?? sessionStorage.getItem('user');
  return raw ? safeJSON<T>(raw, null) : null;
}

/** Limpia usuario de ambos storages */
export function clearUser() {
  localStorage.removeItem('user');
  sessionStorage.removeItem('user');
}

/** Limpia por completo credenciales y usuario */
export function clearAuth() {
  clearToken();
  clearUser();
}

/* ==================== JWT / Roles / Autenticación ==================== */

/** Decodifica el payload de un JWT (sin validar firma) */
export function parseJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return null;
    const json = base64UrlDecode(parts[1]);
    const payload = safeJSON<JwtPayload>(json, null);
    return payload;
  } catch {
    return null;
  }
}

/** Obtiene los roles desde el token (o desde el proporcionado) */
export function getRoles(token?: string): string[] {
  const t = token ?? getToken();
  if (!t) return [];
  const payload = parseJwt(t);
  // Normaliza a array de strings
  const roles = (payload?.roles ?? []) as unknown[];
  return Array.isArray(roles)
    ? roles.map((r) => (typeof r === 'string' ? r : (r as any)?.code || (r as any)?.name)).filter(Boolean) as string[]
    : [];
}

/** ¿El usuario tiene alguno de los roles requeridos? (case-sensitive por defecto) */
export function hasAnyRole(required: string[], token?: string): boolean {
  if (!required?.length) return false;
  const roles = getRoles(token);
  return required.some((r) => roles.includes(r));
}

/** Variante case-insensitive de hasAnyRole */
export function hasAnyRoleCI(required: string[], token?: string): boolean {
  if (!required?.length) return false;
  const have = getRoles(token).map((r) => r.toUpperCase());
  return required.some((r) => have.includes(String(r).toUpperCase()));
}

/** Atajos comunes */
export const isDeviozGerente = (token?: string) => hasAnyRoleCI(['DEVIOZ_GERENTE'], token);
export const isDeviozAdmin   = (token?: string) => hasAnyRoleCI(['DEVIOZ_ADMIN'], token);

/** ¿El token está presente y (si tiene exp) no está vencido? */
export function isAuthenticated(): boolean {
  const t = getToken();
  if (!t) return false;
  const payload = parseJwt(t);
  if (!payload) return false;
  if (!payload.exp) return true; // si no viene exp, asumimos válido
  return Math.floor(Date.now() / 1000) < payload.exp;
}

/* ==================== Calidad de vida para la app ==================== */

/**
 * Intenta hidratar user desde el token si no existe en storage.
 * Útil después de login si el backend solo devuelve JWT.
 */
export function hydrateUserFromToken() {
  const t = getToken();
  if (!t) return;
  const existing = getUser();
  if (existing) return;
  const payload = parseJwt(t);
  if (!payload) return;
  // Guardamos un shape mínimo para que el layout no “parpadee”
  setUser(
    {
      id: payload.sub,
      company_id: payload.company_id,
      roles: payload.roles ?? [],
    },
    // si el token está en localStorage, asumimos remember=true
    !!localStorage.getItem('token')
  );
}
